import { Injectable, signal, computed, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Observable, tap } from 'rxjs';
import {
    User,
    Role,
    Permission,
    AuthApiResponse,
    LoginRequest,
} from '../../shared/models/auth.models';
import { AuthApiService } from './auth-api.service';

const STORAGE_KEYS = {
    CURRENT_USER: 'dashboard_current_user',
    API_TOKEN: 'dashboard_api_token',
};

@Injectable({ providedIn: 'root' })
export class AuthService {
    private platformId = inject(PLATFORM_ID);
    private authApiService = inject(AuthApiService);

    // Signals for reactive state
    private _currentUser = signal<User | null>(null);

    // Public readonly signals
    readonly currentUser = this._currentUser.asReadonly();

    readonly isLoggedIn = computed(() => this._currentUser() !== null);
    readonly isAdmin = computed(() => {
        const user = this._currentUser();
        return user ? user.role?.name === 'Admin' : false;
    });

    readonly currentRole = computed(() => {
        const user = this._currentUser();
        if (!user) return null;
        return user.role || null;
    });

    readonly currentPermissions = computed(() => {
        const role = this.currentRole();
        if (!role) return [];
        return role.permissions.map((p: Permission) => p.name);
    });

    readonly isImpersonatingSeo = computed(() => {
        if (!isPlatformBrowser(this.platformId)) return false;
        return localStorage.getItem('view_as_seo') === 'true';
    });

    readonly isOriginalAdmin = computed(() => {
        if (!isPlatformBrowser(this.platformId)) return false;
        return localStorage.getItem('original_admin_user') !== null || this.isAdmin();
    });

    constructor() {
        if (isPlatformBrowser(this.platformId)) {
            this.loadCurrentUser();
        }
    }

    private loadCurrentUser(): void {
        const saved = localStorage.getItem(STORAGE_KEYS.CURRENT_USER);
        if (saved) {
            try {
                let user = JSON.parse(saved) as User;
                if (localStorage.getItem('view_as_seo') === 'true') {
                    const originalSaved = localStorage.getItem('original_admin_user');
                    if (originalSaved) {
                        const originalUser = JSON.parse(originalSaved) as User;
                        user = {
                            ...originalUser,
                            role: {
                                ...originalUser.role,
                                name: 'seo'
                            }
                        };
                    }
                }
                this._currentUser.set(user);
            } catch {
                localStorage.removeItem(STORAGE_KEYS.CURRENT_USER);
            }
        }
    }

    // ─── Auth Methods ─────────────────────────────────────────

    login(credentials: LoginRequest): Observable<AuthApiResponse> {
        return this.authApiService.login(credentials).pipe(
            tap((response) => {
                if (response && response.token) {
                    this._currentUser.set(response.user);
                    if (isPlatformBrowser(this.platformId)) {
                        localStorage.setItem(STORAGE_KEYS.CURRENT_USER, JSON.stringify(response.user));
                        localStorage.setItem(STORAGE_KEYS.API_TOKEN, response.token);
                    }
                }
            })
        );
    }

    logout(): void {
        // Fire and forget the API logout, then clear session immediately
        const token = this.getToken();
        if (token) {
            this.authApiService.logout().subscribe({
                error: () => { } // silently ignore errors on logout
            });
        }
        this.clearSession();
    }

    forgetPassword(email: string): Observable<any> {
        return this.authApiService.forgetPassword(email);
    }

    resetPassword(data: any): Observable<any> {
        return this.authApiService.resetPassword(data);
    }

    getUserDetails(): Observable<any> {
        return this.authApiService.getUserDetails().pipe(
            tap((res: any) => {
                const user = res.user || res;
                this._currentUser.set(user);
                if (isPlatformBrowser(this.platformId)) {
                    localStorage.setItem(STORAGE_KEYS.CURRENT_USER, JSON.stringify(user));
                }
            })
        );
    }

    firstTimeChangePassword(password: string, password_confirmation: string): Observable<any> {
        return this.authApiService.firstTimeChangePassword(password, password_confirmation);
    }

    getToken(): string | null {
        if (isPlatformBrowser(this.platformId)) {
            return localStorage.getItem(STORAGE_KEYS.API_TOKEN);
        }
        return null;
    }

    hasPermission(permissionName: string): boolean {
        const user = this._currentUser();
        if (!user) return false;
        if (user.role?.name === 'Admin') return true;
        const permissions = this.currentPermissions();
        return permissions.includes(permissionName);
    }

    updateCurrentUserSession(user: User): void {
        this._currentUser.set(user);
        if (isPlatformBrowser(this.platformId)) {
            localStorage.setItem(STORAGE_KEYS.CURRENT_USER, JSON.stringify(user));
        }
    }

    viewAsSeo(): void {
        const user = this._currentUser();
        if (!user || user.role?.name !== 'Admin') return;
        
        if (isPlatformBrowser(this.platformId)) {
            localStorage.setItem('original_admin_user', JSON.stringify(user));
            localStorage.setItem('view_as_seo', 'true');
        }
        
        const seoUser: User = {
            ...user,
            role: {
                ...user.role,
                name: 'seo'
            }
        };
        this._currentUser.set(seoUser);
    }

    exitSeoView(): void {
        if (isPlatformBrowser(this.platformId)) {
            const saved = localStorage.getItem('original_admin_user');
            if (saved) {
                try {
                    const originalUser = JSON.parse(saved) as User;
                    this._currentUser.set(originalUser);
                } catch {
                    // fallback
                }
            }
            localStorage.removeItem('original_admin_user');
            localStorage.removeItem('view_as_seo');
        }
    }

    private clearSession(): void {
        this._currentUser.set(null);
        if (isPlatformBrowser(this.platformId)) {
            localStorage.removeItem(STORAGE_KEYS.CURRENT_USER);
            localStorage.removeItem(STORAGE_KEYS.API_TOKEN);
            localStorage.removeItem('original_admin_user');
            localStorage.removeItem('view_as_seo');
        }
    }
}
